import { ReactElement } from 'react';
import { Message, useMessage } from '@messageformat/react';
import { space } from '@op/opds.tokens';
import { Col, Row, Tab, Tabs } from '@op/opds.opux';
import { IconInformation } from '@op/opds.opux-icons/indicator';
import TimeSpan from './TimeSpan';
import ExampleCostItems from './ExampleCostItems';
import CourseTaskHeader from '../CourseTaskHeader';
import SavingsCalculator from './SavingsCalculator';
import ProfitCalculator from './ProfitCalculator';
import DreamPlanner from './DreamPlanner';
import { ReactComponent as Image002 } from '../../images/OP_symbolikuvitus_128_002.svg';
import { ReactComponent as Image003 } from '../../images/OP_symbolikuvitus_128_003.svg';
import { ReactComponent as Image026 } from '../../images/OP_symbolikuvitus_128_026.svg';
import { ReactComponent as Image050 } from '../../images/OP_symbolikuvitus_128_050.svg';
import CourseHeader from '../CourseHeader';
import CourseComplete from '../CourseComplete';
import BulletList from './Bulletlist';
import { useIsMobile } from '../../util/mediaQuery';
import TextComponent from '../TextComponent';
import parseMsgContent from '../../util/parseMsgContent';
import CourseContent from '../CourseContent';

const Course4 = (): ReactElement => {
  const isMobile = useIsMobile();

  const spanIntroText = useMessage('course4.span_intro');
  const savingPlanText = useMessage('course4.saving_plan.intro');
  const firstColumnArray = useMessage('course4.saving_plan.plan1.body2');
  const secondColumnArray = useMessage('course4.saving_plan.plan2.body');
  const firstExampleTitle = useMessage('course4.monthly_costs.example1.title');
  const secondExampleTitle = useMessage('course4.monthly_costs.example2.title');
  const firstTaskIntro = useMessage('course4.question1.intro');
  const secondTaskIntro = useMessage('course4.question2.intro');
  const thirdTaskIntro = useMessage('course4.question3.intro');
  const investmentGoals = useMessage('course4.investment_goals');
  const parsedSpan = parseMsgContent(spanIntroText);
  const parsedSaving = parseMsgContent(savingPlanText);
  const parsedFirsTask = parseMsgContent(firstTaskIntro);
  const parsedSecondTask = parseMsgContent(secondTaskIntro);
  const parsedThirdTask = parseMsgContent(thirdTaskIntro);
  const parsedGoals = parseMsgContent(investmentGoals);
  const costArray1 = ['950', '400', '50', '100', '200', '400'];
  const costArray2 = ['900', '50', '100', '50', '200'];

  return (
    <div style={{ marginBottom: space.default }}>
      <CourseHeader
        courseTitleId="course4.title"
        courseHeaderText="how_to_start"
        Image={Image002}
        courseNumber={4}
      />
      <TextComponent messageId="course4.introduction" twoColumns />
      <CourseContent content={parsedSpan} headingAs="h2" />
      <div style={{ paddingTop: space.xl, paddingBottom: space.xxxl }}>
        <TimeSpan />
      </div>
      <div style={{ paddingTop: space.xxs }}>
        <Image026 className="investoracademy-small-svg" aria-hidden="true" />
      </div>
      <CourseContent content={parsedSaving} headingAs="h2" />
      <Row
        style={{
          marginBottom: space.xxl,
          marginLeft: 0,
          marginRight: 0,
          overflow: 'hidden',
        }}
      >
        <Col lg={6} sm={12} className="investoracademy-multi-column-left-small-padding">
          <div
            className="investoracademy-number"
            style={{
              marginTop: space.default,
              marginBottom: space.default,
            }}
          >
            1
          </div>
          <h3 className="opds-styles-v1-text-title-large" style={{ textAlign: 'center' }}>
            <Message id="course4.saving_plan.plan1.title" />
          </h3>
          <p
            className="opds-styles-v1-text-body-medium"
            style={{ paddingTop: space.default, paddingBottom: '0px' }}
          >
            <Message id="course4.saving_plan.plan1.body1" />
          </p>
          <BulletList
            array={firstColumnArray}
            messageId="course4.saving_plan.plan1.body2"
            listStyleType="disc"
          />
        </Col>
        <Col lg={6} sm={12} className="investoracademy-multi-column-right-small-padding">
          <div
            className="investoracademy-number"
            style={{
              marginTop: space.default,
              marginBottom: space.default,
            }}
          >
            2
          </div>
          <h3 className="opds-styles-v1-text-title-large" style={{ textAlign: 'center' }}>
            <Message id="course4.saving_plan.plan2.title" />
          </h3>
          <BulletList
            array={secondColumnArray}
            messageId="course4.saving_plan.plan2.body"
            listStyleType="decimal"
          />
        </Col>
      </Row>
      <div style={{ paddingBottom: space.default }}>
        <Image003 className="investoracademy-small-svg" aria-hidden="true" />
      </div>
      <h2 className="investoracademy-title-hero">
        <Message id="course4.monthly_costs.title" />
      </h2>
      <div style={{ paddingBottom: space.xxl }}>
        <Tabs
          small
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 0 0',
            paddingTop: space.default,
          }}
        >
          <Tab
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
            label={firstExampleTitle}
          >
            <Row style={{ margin: 0 }}>
              <Col
                sm={12}
                md={6}
                style={{ padding: 0, paddingRight: !isMobile ? space.xs : 0 }}
              >
                <ExampleCostItems
                  messageId="course4.monthly_costs.example1.items"
                  costArray={costArray1}
                  total={2100}
                />
              </Col>
              <Col
                sm={12}
                md={6}
                style={{ padding: 0, paddingLeft: !isMobile ? space.xs : 0 }}
                className="opux-v6__opds"
              >
                <TextComponent
                  messageId="course4.monthly_costs.example1.body"
                  twoColumns={false}
                />
              </Col>
            </Row>
          </Tab>
          <Tab
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
            label={secondExampleTitle}
          >
            <Row style={{ margin: 0 }}>
              <Col
                sm={12}
                md={6}
                style={{ padding: 0, paddingRight: !isMobile ? space.xs : 0 }}
              >
                <ExampleCostItems
                  messageId="course4.monthly_costs.example2.items"
                  costArray={costArray2}
                  total={1300}
                />
              </Col>
              <Col
                sm={12}
                md={6}
                style={{ padding: 0, paddingLeft: !isMobile ? space.xs : 0 }}
                className="opux-v6__opds"
              >
                <TextComponent
                  messageId="course4.monthly_costs.example2.body"
                  twoColumns={false}
                />
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </div>
      <CourseTaskHeader questionNumber={1} totalQuestions={3} />
      <div style={{ marginTop: space.large }}>
        <CourseContent content={parsedFirsTask} />
      </div>
      <div
        className="investoracademy-vertical-flex-align"
        style={{ paddingTop: space.default, paddingBottom: space.xxl }}
      >
        <SavingsCalculator messageId="course4.question1.calculator.columns" />
      </div>
      <Image050 className="investoracademy-small-svg" aria-hidden="true" />
      <div style={{ paddingBottom: space.xxl }}>
        <CourseContent content={parsedGoals} headingAs="h2" />
      </div>
      <CourseTaskHeader questionNumber={2} totalQuestions={3} />
      <div style={{ marginTop: space.large }}>
        <CourseContent content={parsedSecondTask} />
      </div>
      <div
        className="investoracademy-vertical-flex-align"
        style={{ paddingBottom: space.xxl, paddingTop: space.xl }}
      >
        <DreamPlanner />
      </div>
      <CourseTaskHeader questionNumber={3} totalQuestions={3} />
      <div style={{ marginTop: space.large, marginBottom: space.large }}>
        <CourseContent content={parsedThirdTask} />
      </div>
      <ProfitCalculator />
      <p
        className="opds-styles-v1-text-label-small"
        style={{ display: 'flex', alignItems: 'flex-start', paddingTop: space.default }}
      >
        <IconInformation />
        <Message id="course4.question3.indicator" />
      </p>
      <CourseComplete messageId="course4.recap" />
    </div>
  );
};

export default Course4;
